import type { Review } from '@/scopes/products/user-reviews/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useState } from 'react'
import { useIntl } from '@/intl'
import { ReadMore } from '@/scopes/common/components/ReadMore'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { createUserReviewsEvent } from '@/scopes/products/user-reviews/infrastructure/analytics'
import ReviewerInfo from './ReviewerInfo'
import { ReviewScore } from './ReviewScore'
import { TranslatedByAtribution } from './TranslatedByAttribution'
import { TranslatedText } from './TranslatedText'
import styles from './UserReview.module.css'
import { getFlagCodeByCountry, getPartyCompositionType, resolveLanguageCode } from './utils'

export function UserReview({
  date,
  rating,
  description,
  reviewLanguageCode,
  userName,
  partyCompositionGroupId,
  id,
  departureDate,
  userCountryCode,
}: Review) {
  const intl = useIntl()
  const [showTranslatedText, setShowTranslatedText] = useState(false)
  const { sendEvent } = useAnalytics()
  const siteLanguage = resolveLanguageCode(useConfiguration('locale'))

  // If the date is empty (shown as 1970-01-01) we want to show the departure date instead of the review date
  const resolveDate = () => {
    const invalidDate = new Date('1970-01-01')

    if (Number(date) === Number(invalidDate))
      return departureDate

    return intl.formatRelativeDate(date)
  }

  const handleToggleTranslation = () => {
    // There is a typo in the event name "reviewTransalted" that we need to keep for backwards compatibility. Once we remove legacy components we can fix it.
    sendEvent(
      createUserReviewsEvent({
        description: 'Show review translations',
        element: 'Clickable text',
        result: showTranslatedText ? 'original' : 'translated',
        ecommerce: {
          reviewTransalted: {
            id,
            overallRating: Number.parseFloat(rating),
            country: reviewLanguageCode,
            partyComposition: intl.formatMessage({
              id: `reviews_party_composition_${getPartyCompositionType(partyCompositionGroupId)}_label`,
            }),
          },
        },
      }),
    )
    setShowTranslatedText(!showTranslatedText)
  }

  return (
    <div className={styles.card}>
      <div className={styles.headerWrapper}>
        <ReviewScore rating={rating} />
        <Typography variant="body-m-regular-tight" className={styles.strongText}>{resolveDate()}</Typography>
      </div>
      {showTranslatedText && <TranslatedText>{description}</TranslatedText>}
      {!showTranslatedText && (
        <ReadMore charactersShown={255} readMoreText={intl.formatMessage({ id: 'reviews_read_more_label' })}>
          {description || description !== '' ? description : intl.formatMessage({ id: 'reviews_no_text' })}
        </ReadMore>
      )}
      {reviewLanguageCode !== siteLanguage && showTranslatedText && (
        <div className={styles.translateButtonWrapper}>
          <Button variant="transparentNeutral" onClick={handleToggleTranslation} size="XS">
            <Button.Icon glyph="LanguageTranslate" />
            {intl.formatMessage({ id: 'reviews_translation_original' })}
          </Button>
          <TranslatedByAtribution />
        </div>
      )}
      {reviewLanguageCode !== siteLanguage && !showTranslatedText && (
        <div className={styles.translateButtonWrapper}>
          <Button variant="transparentNeutral" onClick={handleToggleTranslation} size="XS">
            <Button.Icon glyph="LanguageTranslate" />
            {intl.formatMessage({ id: 'reviews_translation_translate' })}
          </Button>
        </div>
      )}
      <div className={styles.reviewerInfoWrapper}>
        <ReviewerInfo
          reviewerName={userName || intl.formatMessage({ id: 'reviews_anonymous_user' })}
          flagCode={getFlagCodeByCountry(userCountryCode)}
          partyCompositionLabel={intl.formatMessage({ id: `reviews_party_composition_${getPartyCompositionType(partyCompositionGroupId)}_label` })}
          isBigWrapper
        />
      </div>
    </div>
  )
}
